import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import {
  createTenant,
  deleteUser,
  listTenants,
  listUsers,
  resetPassword,
  type AdminUser,
  type Tenant
} from "../../lib/auth";
import { IconCopy, IconRefresh } from "../icons";
import { ConfirmDialog } from "./ConfirmDialog";

/** Human label for a role value shown in the table. */
const ROLE_LABEL: Record<string, string> = {
  end_user: "终端用户",
  agent: "客服",
  admin: "管理员"
};

/** A pending sensitive action awaiting confirmation (#84 sensitive-op guard). */
interface Pending {
  kind: "reset" | "delete";
  user: AdminUser;
}

/**
 * Admin-only user management page (#84). Lists every user and lets the admin
 * reset a password (generated server-side, shown once with a "请记录" reminder)
 * or delete an account, and add a predefined tenant (#93). Both reset and delete
 * are sensitive and run only after an explicit confirmation.
 */
export function AdminUserManagement({
  selfId,
  onClose
}: {
  selfId: string;
  onClose: () => void;
}): JSX.Element {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // The just-reset credential to surface once for the admin to record (#84).
  const [reveal, setReveal] = useState<{ username: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);
  // Sensitive action pending confirmation, and whether it is running.
  const [pending, setPending] = useState<Pending | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  // Add-tenant form state.
  const [newTenant, setNewTenant] = useState("");
  const [tenantBusy, setTenantBusy] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [u, t] = await Promise.all([listUsers(), listTenants()]);
      setUsers(u);
      setTenants(t);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Run the confirmed sensitive action (reset or delete).
  const onConfirm = async (): Promise<void> => {
    if (pending === null || actionBusy) return;
    setActionBusy(true);
    setError(null);
    const { kind, user } = pending;
    if (kind === "reset") {
      setCopied(false);
      const r = await resetPassword(user.id);
      if (r.ok) setReveal({ username: user.username, password: r.password });
      else setError(r.reason);
    } else {
      const r = await deleteUser(user.id);
      if (r.ok) {
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
      } else {
        setError(r.reason);
      }
    }
    setActionBusy(false);
    setPending(null);
  };

  const onCopy = (): void => {
    if (reveal === null) return;
    void navigator.clipboard?.writeText(reveal.password).then(() => setCopied(true));
  };

  const onAddTenant = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (tenantBusy || newTenant.trim() === "") return;
    setTenantBusy(true);
    setError(null);
    const r = await createTenant(newTenant.trim());
    setTenantBusy(false);
    if (r.ok) {
      setNewTenant("");
      const t = await listTenants();
      setTenants(t);
    } else {
      setError(r.reason);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-users-card" onClick={(e) => e.stopPropagation()}>
        <div className="admin-users-head">
          <h1 className="login-title">用户管理</h1>
          <button className="icon-btn" type="button" aria-label="刷新" onClick={() => void refresh()}>
            <IconRefresh size={16} />
          </button>
        </div>

        {reveal !== null && (
          <div className="admin-reset-reveal">
            <div className="admin-reset-title">已为 {reveal.username} 重置密码 · 请记录此密码</div>
            <div className="admin-reset-row">
              <code className="admin-reset-pwd">{reveal.password}</code>
              <button className="admin-copy-btn" type="button" onClick={onCopy}>
                <IconCopy size={14} /> {copied ? "已复制" : "复制"}
              </button>
            </div>
            <div className="admin-reset-hint">仅显示一次，请立即转交给用户并妥善保存。</div>
            <button className="admin-cancel" type="button" onClick={() => setReveal(null)}>
              我已记录
            </button>
          </div>
        )}

        {error !== null && <div className="login-error">{error}</div>}

        <div className="admin-users-list">
          {loading ? (
            <div className="admin-users-empty">加载中…</div>
          ) : users.length === 0 ? (
            <div className="admin-users-empty">还没有用户</div>
          ) : (
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>用户名</th>
                  <th>角色</th>
                  <th>租户</th>
                  <th>状态</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{ROLE_LABEL[u.role] ?? u.role}</td>
                    <td>{u.tenant}</td>
                    <td>{u.isActive ? "启用" : "停用"}</td>
                    <td className="admin-row-actions">
                      <button
                        className="admin-row-btn"
                        type="button"
                        disabled={actionBusy}
                        onClick={() => setPending({ kind: "reset", user: u })}
                      >
                        重置密码
                      </button>
                      {/* The admin cannot delete their own account (no last-admin lockout). */}
                      {u.id !== selfId && (
                        <button
                          className="admin-row-btn admin-row-btn-danger"
                          type="button"
                          disabled={actionBusy}
                          onClick={() => setPending({ kind: "delete", user: u })}
                        >
                          删除
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <form className="admin-tenant-form" onSubmit={(e) => void onAddTenant(e)}>
          <div className="login-sub">新增租户名称(预定义后可在创建用户时从下拉选择)</div>
          <div className="admin-tenant-row">
            <input
              type="text"
              placeholder="租户名称(如 testingOrg)"
              value={newTenant}
              onChange={(e) => setNewTenant(e.target.value)}
            />
            <button className="admin-row-btn" type="submit" disabled={tenantBusy || newTenant.trim() === ""}>
              {tenantBusy ? "新增中…" : "新增"}
            </button>
          </div>
          <div className="admin-tenant-current">
            当前租户：{tenants.length === 0 ? "（无）" : tenants.map((t) => t.name).join("、")}
          </div>
        </form>

        <button className="admin-cancel" type="button" onClick={onClose}>
          关闭
        </button>
      </div>

      {pending !== null && (
        <ConfirmDialog
          title={pending.kind === "delete" ? "删除用户" : "重置密码"}
          message={
            pending.kind === "delete"
              ? `确定删除用户 ${pending.user.username}？该账户将无法再登录，此操作不可撤销（其对话历史会保留）。`
              : `确定为 ${pending.user.username} 重置密码？其当前密码将立即失效，并生成一个新密码。`
          }
          confirmLabel={pending.kind === "delete" ? "删除" : "重置"}
          destructive={pending.kind === "delete"}
          busy={actionBusy}
          onConfirm={() => void onConfirm()}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}
